import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import type { ServerCredentialInfo } from '@/api/type-server'
import ArrowLeft from '@/assets/icons/arrow-left.svg?react'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { message, Trans } from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { Button } from '@/components/base/Button'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { Pagination } from '@/components/base/Pagination'
import { EmptyView } from '@/components/EmptyView'
import { getCredentialStatus, isCredentialExpired } from '@/utils/credential'

import { BindResourcesModal } from './BindResourcesModal'
import { CredentialModal } from './CredentialModal'
import { RotateCredentialModal } from './RotateCredentialModal'

interface AccessKeysPanelProps {
  onBack: () => void
}

const PAGE_SIZE = 10

export function AccessKeysPanel({ onBack }: AccessKeysPanelProps) {
  const { t } = useTranslation('accessKeys')
  const [credentials, setCredentials] = useState<ServerCredentialInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [rotateItem, setRotateItem] = useState<ServerCredentialInfo | null>(null)
  const [editItem, setEditItem] = useState<ServerCredentialInfo | null>(null)
  const [bindItem, setBindItem] = useState<ServerCredentialInfo | null>(null)

  const fetchList = useCallback(
    async (p = page) => {
      setLoading(true)
      try {
        const res = await API.getCredentialList({ page: p, per_page: PAGE_SIZE })
        setCredentials(res.credentials || [])
        setTotal(res.pagination?.total || 0)
        setEditItem(
          (item) => item && (res.credentials?.find((c) => c.resID === item.resID) || item),
        )
        setBindItem(
          (item) => item && (res.credentials?.find((c) => c.resID === item.resID) || item),
        )
      } finally {
        setLoading(false)
      }
    },
    [page],
  )

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const handleToggleEnabled = async (item: ServerCredentialInfo) => {
    const res = await API.updateCredential({
      res_id: item.resID,
      title: item.title,
      description: item.description,
      valid_duration_sec: item.validDurationSec,
      enabled: !item.enabled,
    })
    if (res.code !== 0) return
    message.success(item.enabled ? t('toggle.disabled') : t('toggle.enabled'))
    fetchList()
  }

  const handleRotate = (item: ServerCredentialInfo) => {
    setRotateItem(item)
  }

  const handleDelete = async (item: ServerCredentialInfo) => {
    try {
      await Modal.confirm({
        title: t('deleteModal.title'),
        content: (
          <p>
            {t('deleteModal.content')}{' '}
            <span className="text-error">{t('deleteModal.cannotBeUndone')}</span>.{' '}
          </p>
        ),
      })
      const res = await API.deleteCredential({ res_id: item.resID })
      if (res.code !== 0) return
      message.success(t('deleteModal.deleted'))
      fetchList()
    } catch {
      // cancelled
    }
  }

  const formatTime = (ts: { seconds: number }) => {
    if (!ts?.seconds) return '-'
    return dayjs.unix(ts.seconds).format('YYYY-MM-DD HH:mm:ss')
  }

  const handleCopyAK = (ak: string) => {
    navigator.clipboard.writeText(ak)
    message.success(t('copied'))
  }

  const getScopeLabel = () => t('scope.autoDeploy')

  return (
    <div className="card bg-panel-white h-full flex flex-col overflow-hidden">
      {/* Title Bar */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray100">
        <div
          className="w-8 h-8 rounded-md bg-panel-gray-100 flex items-center justify-center cursor-pointer hover:bg-panel-gray-200"
          onClick={onBack}
        >
          <ArrowLeft className="w-5 h-5 text-tertiary" />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <span className="h6 text-tertiary">
            <Trans i18nKey="profile.accountSetting">Account Setting</Trans>
          </span>
          <span className="h6 text-primary">/</span>
          <span className="h6 text-primary">{t('title')}</span>
        </div>
        <Button type="secondary" onClick={() => setCreateModalOpen(true)} className="min-w-[88px]">
          {t('createBtn')}
        </Button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto mt-8">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Loading />
          </div>
        ) : credentials.length === 0 ? (
          <EmptyView content={t('noData')} />
        ) : (
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-panel-gray-100">
                <th className="px-4 py-3 text-left t-sm text-secondary min-w-[80px]">
                  {t('table.name')}
                </th>
                <th className="px-4 py-3 text-left t-sm text-secondary min-w-[100px]">
                  {t('table.description')}
                </th>
                <th className="px-4 py-3 text-left t-sm text-secondary min-w-[180px]">
                  {t('table.accessKey')}
                </th>
                <th className="px-4 py-3 text-left t-sm text-secondary min-w-[100px]">
                  {t('table.scope')}
                </th>
                <th className="px-4 py-3 text-left t-sm text-secondary min-w-[150px]">
                  {t('table.expiresAt')}
                </th>
                <th className="px-4 py-3 text-left t-sm text-secondary min-w-[150px]">
                  {t('table.createTime')}
                </th>
                <th className="px-4 py-3 text-left t-sm text-secondary min-w-[80px]">
                  {t('table.status')}
                </th>
                <th className="px-4 py-3 text-center t-sm text-secondary min-w-[60px]">
                  {t('table.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {credentials.map((item) => (
                <tr key={item.resID} className="border-b border-gray100">
                  <td className="px-4 py-4 t-sm text-primary max-w-[120px]">
                    <Tooltip title={item.title} overlayStyle={{ maxWidth: 320 }}>
                      <div className="truncate">{item.title}</div>
                    </Tooltip>
                  </td>
                  <td className="px-4 py-4 t-sm text-primary max-w-[150px]">
                    <Tooltip
                      title={<span style={{ whiteSpace: 'pre-wrap' }}>{item.description}</span>}
                      overlayStyle={{ maxWidth: 320 }}
                    >
                      <div className="truncate">{item.description || '-'}</div>
                    </Tooltip>
                  </td>
                  <td className="px-4 py-4 t-sm text-primary">
                    <div className="flex items-center gap-1 max-w-[180px]">
                      <span className="truncate">{item.accessKey}</span>
                      <CopyIcon
                        className="w-4 h-4 text-tertiary cursor-pointer shrink-0 hover:text-secondary"
                        onClick={() => handleCopyAK(item.accessKey)}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-4 t-sm text-primary max-w-[140px]">
                    <Tooltip title={getScopeLabel()} overlayStyle={{ maxWidth: 320 }}>
                      <div className="truncate">{getScopeLabel()}</div>
                    </Tooltip>
                  </td>
                  <td
                    className={`px-4 py-4 t-sm ${isCredentialExpired(item) ? 'text-error' : 'text-primary'}`}
                  >
                    {formatTime(item.expiresAt)}
                  </td>
                  <td className="px-4 py-4 t-sm text-tertiary">{formatTime(item.createdAt)}</td>
                  <td className="px-4 py-4 t-sm">
                    <span
                      className={
                        isCredentialExpired(item)
                          ? 'text-error'
                          : item.enabled
                            ? 'text-active'
                            : 'text-tertiary'
                      }
                      title={isCredentialExpired(item) ? t('status.expiredHint') : undefined}
                    >
                      {t(`status.${getCredentialStatus(item)}`)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <ActionMenu
                      items={[
                        {
                          label: t('actions.edit'),
                          onClick: () => setEditItem(item),
                          type: 'highlight',
                        },
                        {
                          label: item.enabled ? t('actions.disable') : t('actions.enable'),
                          onClick: () => handleToggleEnabled(item),
                        },
                        {
                          label: t('actions.bindResources'),
                          onClick: () => setBindItem(item),
                        },
                        {
                          label: t('actions.rotate'),
                          onClick: () => handleRotate(item),
                        },
                        {
                          label: t('actions.delete'),
                          onClick: () => handleDelete(item),
                          type: 'danger',
                        },
                      ]}
                      popupContainer="body"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex justify-end pt-4">
          <Pagination size={PAGE_SIZE} total={total} current={page} onChange={setPage} />
        </div>
      )}

      {/* Create Modal */}
      {createModalOpen && (
        <CredentialModal onClose={() => setCreateModalOpen(false)} onSaved={() => fetchList()} />
      )}

      {/* Edit Modal */}
      {editItem && (
        <CredentialModal
          credential={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => fetchList()}
        />
      )}

      {/* Bind Resources Modal */}
      {bindItem && (
        <BindResourcesModal
          credential={bindItem}
          onClose={() => setBindItem(null)}
          onBound={() => fetchList()}
        />
      )}

      {/* Rotate Modal */}
      {rotateItem && (
        <RotateCredentialModal
          item={rotateItem}
          onClose={() => setRotateItem(null)}
          onRotated={() => fetchList()}
        />
      )}
    </div>
  )
}
